import { FilterDescriptorEx, ODataExSettings } from './odata';
// Use the ESM build (`dist/es`) instead of the CJS build (`dist/npm`).
// `wrapIf` is not re-exported from `@progress/kendo-data-query`'s main
// entry, so we must reach into the sub-path. The CJS build performs
// internal `require('./utils')` calls that the NS-vite HMR runtime
// cannot satisfy synchronously (modules are served over HTTP), which
// triggered `[ns-hmr][require-guard] require of URL: ...
// kendo-data-query/dist/npm/utils.js` warnings on every cold boot. The
// ESM build (`dist/es`) uses real ESM imports that Vite resolves
// normally, eliminating the warning. Both files export the same
// `wrapIf` function with identical behavior — see
// `node_modules/@progress/kendo-data-query/dist/{npm,es}/filter-serialization.common.{js,d.ts}`.
import { wrapIf } from '@progress/kendo-data-query/dist/es/filter-serialization.common';

//
// Core
export type Transformation = (
  f: FilterDescriptorEx,
  s: ODataExSettings
) => FilterDescriptorEx;

const usesLocalDateSearch = (settings: ODataExSettings) =>
  settings.filter.localDateSearch;

//
// transformations over the field property
export const normalizeField: Transformation = (f: FilterDescriptorEx) => {
  f.field = (<string>f.field).replace(/\./g, '/');
  return f;
};

export const toLowerField: Transformation = (
  f: FilterDescriptorEx,
  s: ODataExSettings
) => {
  f.field = wrapIf(() =>
    f.hasOwnProperty('ignoreCase') ? f.ignoreCase : s.filter.globalIgnoreCase
  )`tolower(${f.field})`;
  return f;
};

export const castFieldToDateTimeOffset: Transformation = (
  f: FilterDescriptorEx,
  s: ODataExSettings
) => {
  const isLocalDateSearch = usesLocalDateSearch(s) && !f.exactInstant;
  f.field = wrapIf(() => isLocalDateSearch)`date(${f.field})`;
  return f;
};

export const castFieldToString: Transformation = (f: FilterDescriptorEx) => {
  f.field = wrapIf(() => f.convertToString)`cast(${f.field},'Edm.String')`;
  return f;
};

//
// Transformations over the value
export const quote: Transformation = (f: FilterDescriptorEx) => {
  f.value =
    f.skipQuotes === true
      ? f.value
      : Array.isArray(f.value)
        ? f.value.map((v) => `'${v.replace(/'/g, "''")}'`)
        : (f.value = `'${f.value.replace(/'/g, "''")}'`);
  return f;
};

export const encodeValue: Transformation = (f: FilterDescriptorEx) => {
  f.value = Array.isArray(f.value)
    ? f.value.map((v) => `${encodeURIComponent(v)}`)
    : `${encodeURIComponent(f.value)}`;
  return f;
};

export const toLowerValue: Transformation = (
  f: FilterDescriptorEx,
  s: ODataExSettings
) => {
  f.value = Array.isArray(f.value)
    ? f.value.map(
        (v) =>
          wrapIf(() =>
            f.hasOwnProperty('ignoreValueCase')
              ? f.ignoreValueCase
              : s.filter.ignoreValueCase
          )`tolower(${v})`
      )
    : wrapIf(() =>
        f.hasOwnProperty('ignoreValueCase')
          ? f.ignoreValueCase
          : s.filter.ignoreValueCase
      )`tolower(${f.value})`;
  return f;
};

const formatDateForOData = (value: Date, useLocalDateSearch: boolean) => {
  if (!useLocalDateSearch) {
    return value.toISOString().replace('+', '%2B');
  }

  const pad = (segment: number) => segment.toString().padStart(2, '0');
  const milliseconds = value.getMilliseconds().toString().padStart(3, '0');
  return `${value.getFullYear().toString().padStart(4, '0')}-${pad(
    value.getMonth() + 1
  )}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(
    value.getMinutes()
  )}:${pad(value.getSeconds())}.${milliseconds}`;
};

const formatSingleDate = (
  value,
  settings: ODataExSettings,
  exactInstant = false
) => {
  const isLocalDateSearch = usesLocalDateSearch(settings) && !exactInstant;
  let stringifiedDate = formatDateForOData(value, isLocalDateSearch);

  if (isLocalDateSearch) {
    // Emit Edm.Date literal (YYYY-MM-DD) for date() comparison. Avoids $apply parse errors
    // (unquoted datetime with T was parsed as arithmetic) and ensures backend compares date-to-date.
    stringifiedDate = stringifiedDate.substring(0, 10);
  } else {
    // note: since we allow also non UTC values, we need to encode the (sometimes occuring) + (offset sign) in the ISO8601 stringified date
    stringifiedDate = stringifiedDate.replace('+', '%2B');
  }
  return stringifiedDate;
};

export const formatDateEx: Transformation = (
  f: FilterDescriptorEx,
  settings: ODataExSettings
) => {
  f.value = Array.isArray(f.value)
    ? f.value.map((v) => formatSingleDate(v, settings, f.exactInstant))
    : formatSingleDate(f.value, settings, f.exactInstant);

  return f;
};
